import { Button, Icon, Notification, Typography } from '@smartgift/frontend-toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { MissingRecipientInfoModal } from '@hero/components/modals';
import { configs } from '@hero/configs';
import {
  APPROVAL_REASON_GIFT_CARD,
  APPROVAL_STATUSES,
  AUTOMATION_STATUSES,
  CARDISLE_GREETING_CARD,
  CONTACT_TYPE,
  PRODUCT_TYPES
} from '@hero/constants';
import { useDeleteModalTemplate, useGetTagBasedAutomationRecipient } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { CostSummary } from '@hero/molecules';
import { GiftPreviewBox } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useGetAutomation,
  useStartAutomation,
  useGetAutomationSpendingSummary,
  useDuplicateAutomation,
  useUpdateAutomation,
  useDeleteAutomation,
  useAutomationSubmitToApproval
} from '@hero/services/order-service';
import { useNotification, useValidateRules } from '@hero/store/hooks';
import { getAddonData } from '@hero/utils';
import { getReviewAndActivateRule } from '@hero/validations';

import HeaderNotificationContent from './HeaderNotificationContent';
import { NavigationCenterContent } from './NavigationCenterContent';
import { NavigationEndContent } from './NavigationEndContent';
import * as S from './style';
import ToggleMenu from './ToggleMenu';

function AutomationReviewAndActiveTemplate() {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { mutateAsync: onUpdate } = useUpdateAutomation();

  const { data: automation, refetch: refetchAutomation } = useGetAutomation(
    { code },
    { includeGoLiveChecks: true }
  );
  const { mutateAsync: startAutomation, isLoading: isLoadingStartAutomation } =
    useStartAutomation();
  const { mutateAsync: duplicateAutomation } = useDuplicateAutomation();
  const { mutateAsync: submitToApproval } = useAutomationSubmitToApproval();

  const { showDeleteModal } = useDeleteModalTemplate({
    title: t('areYouSureYouWantDelete', { type: t('automation').toLowerCase() }),
    message: t('deleteAutomationModalDesc'),
    confirmText: 'DELETE AUTOMATION',
    maxLength: 17
  });
  const { mutateAsync: deleteAutomation } = useDeleteAutomation();

  const { validate } = useValidateRules();

  const checkPageIsValid = async () => {
    const schemaRule = getReviewAndActivateRule({
      isPaymentMethodRequired: configs?.paymentMethods?.enabled,
      isEndDateRequired: false, // !isLogoEdApprovalBoxShown
      isInputMessageRequired: !digitalGreetingCard
    });
    const isValid = await validate([
      {
        rule: schemaRule,
        value: {
          ...automation,
          experienceSettings: {
            ...automation.experienceSettings,
            messageParams: {
              ...automation.experienceSettings.messageParams,
              digitalGreetingCard: !!digitalGreetingCard
            }
          }
        }
      }
    ]);
    return isValid;
  };

  const { missingInfoContacts } = useGetTagBasedAutomationRecipient(automation);

  const [isMissingRecipientInfoModalOpen, setIsMissingRecipientInfoModalOpen] = useState(false);

  const hasContactsIds = automation?.contacts?.ids?.length > 0;
  const hasContactsTags = automation?.contacts?.tags?.length > 0;
  const missingInfoContactsLength = missingInfoContacts?.length > 0;
  const isMissingRecipientInfoContacts = hasContactsIds && missingInfoContactsLength;
  const isMissingRecipientInfoTags = hasContactsTags && missingInfoContactsLength;
  const isMissingRecipientInfo = isMissingRecipientInfoContacts || isMissingRecipientInfoTags;

  const onNextButtonClick = useCallback(async () => {
    await startAutomation({
      code: automation.code,
      body: {
        status: AUTOMATION_STATUSES.active
      }
    });
    navigate(ROUTE_PATHS.NEW_AUTOMATION_ORDER_CONFIRMED(automation.code), { replace: true });
  }, [automation, missingInfoContacts]);

  const {
    data: automationSpendingSummary,
    isError: isErrorSpendingSummary,
    isFetching: isFetchingSpendingSummary,
    isLoading: isLoadingSpendingSummary
  } = useGetAutomationSpendingSummary({
    code,
    products: automation?.products
  });

  const orderType = 'automation';
  const contactType = automation?.contacts?.ids?.length ? CONTACT_TYPE.list : CONTACT_TYPE.tags;
  const addingMethods = {
    [CONTACT_TYPE.tags]: {
      tooltip: !automation?.contacts?.tags?.length
        ? t('tooltipSelectTagsBeforeActivating', { orderType })
        : '',
      isActive: !!automation?.contacts?.tags?.length
    },
    [CONTACT_TYPE.list]: {
      tooltip: !automation?.contacts?.ids?.length
        ? t('tooltipSelectContactsBeforeActivating', { orderType })
        : '',
      isActive: !!automation?.contacts?.ids?.length
    }
  };
  const isEGiftCardRequired =
    automation?.approval?.reason === PRODUCT_TYPES.GIFT_CARD &&
    automation?.approval?.status === APPROVAL_STATUSES.required;

  const hasEGiftCardItem = automation?.products?.some(
    p => p?.meta?.productType === PRODUCT_TYPES.GIFT_CARD
  );

  const isApprovalRequired = automation?.approval?.status === APPROVAL_STATUSES.required;
  const isApprovalBoxShown = isApprovalRequired && automation?.status === AUTOMATION_STATUSES.draft;
  const isApprovalPending = automation?.status === APPROVAL_STATUSES.pending_approval;
  const isCancelApprovalBoxShown = automation && isApprovalRequired && isApprovalPending;
  const digitalGreetingCard =
    configs?.experience?.greetingCardEnabled &&
    getAddonData(automation?.addons, CARDISLE_GREETING_CARD);
  const approvalType = automation?.approval?.reason;

  const submitForApproval = async () => {
    if (approvalType === APPROVAL_REASON_GIFT_CARD) {
      await submitToApproval({
        code: code
      });

      showNotification({
        header: t('eGiftCardApprovalPending'),
        customContent: () => (
          <S.CustomApprovalNotificationContent>
            <Notification
              style={{ margin: '8px 0' }}
              status="info"
              message={
                <Trans i18nKey="eGiftCardWasCouldNotAddedRecognition" components={{ b: <b /> }} />
              }
            />
            <Icon name="clock" size="xxl" />
            <Typography>
              <Trans i18nKey="yourApprovalRequestIsReviewed" components={{ b: <b /> }} />
            </Typography>
          </S.CustomApprovalNotificationContent>
        )
      });
    }
  };

  const getPrimaryButton = () => {
    const button = {
      children: t('activate'),
      variant: 'contained',
      color: 'primary',
      content: addingMethods[contactType]?.tooltip,
      onClick: async () => await onNextButtonClick()
    };

    if (isApprovalBoxShown) {
      button.children = t('submitForApproval');
      button.onClick = async () => await submitForApproval();
      // button.loading = isSendToApprovalLoading;
      return button;
    }

    if (isCancelApprovalBoxShown) {
      button.disabled = true;

      return button;
    }

    return button;
  };

  const payAndSendButtonProps = useMemo(
    () => ({
      icon: false,
      loading: isLoadingStartAutomation,
      style: {
        width: '100%'
      },
      ...getPrimaryButton(),
      onClick: async () => {
        const isPageValid = await checkPageIsValid();
        if (!isPageValid) return;
        await getPrimaryButton().onClick();
      }
    }),
    [missingInfoContacts, automation, isApprovalBoxShown, isLoadingStartAutomation]
  );

  return (
    <PageLayout
      header={{
        title: t('reviewAndActive'),
        showBorder: true,
        components: {
          CenterContent: NavigationCenterContent,
          EndContent: NavigationEndContent
        },
        style: {
          backgroundColor: 'var(--color-bg-weakest2)'
        }
      }}
      navigation={{
        rightButton: payAndSendButtonProps,
        leftButton: {
          children: t('goToName', { name: t('automations') }),
          onClick: () => navigate(ROUTE_PATHS.MY_AUTOMATION_LIST)
        }
      }}
    >
      <HeaderNotificationContent
        iseGiftCardApprovalBoxShown={isEGiftCardRequired && hasEGiftCardItem}
      />

      {isMissingRecipientInfo && (
        <Notification
          status="info"
          style={{ marginBottom: 24 }}
          message={
            <Trans
              i18nKey="automationContactMissingDatesWarning"
              values={{
                count: missingInfoContacts?.length,
                type:
                  automation?.type === 'birthday'
                    ? t('birthdays').toLocaleLowerCase()
                    : t('workAnniversaries').toLocaleLowerCase()
              }}
              components={{
                b: <b />,
                button: (
                  <Button
                    variant="link"
                    color="secondary"
                    onClick={() => setIsMissingRecipientInfoModalOpen(true)}
                  />
                )
              }}
            />
          }
        />
      )}
      <Typography variant="subtitle4Bold" style={{ marginBottom: 16, textAlign: 'center' }}>
        {t('automationReviewAndActiveHeaderSubTitle')}
      </Typography>
      <S.PageContent>
        <ToggleMenu refetchAutomation={refetchAutomation} />
        <S.RightColumn>
          <CostSummary
            {...(automationSpendingSummary || {})}
            status={automation?.status}
            payAndSendButtonProps={payAndSendButtonProps}
            featureType="automation"
            feature={automation}
            isLoadingSpendingSummary={isFetchingSpendingSummary || isLoadingSpendingSummary}
            isError={isErrorSpendingSummary}
            contactType={
              automation?.contacts?.ids?.length > 0 ? CONTACT_TYPE.list : CONTACT_TYPE.tags
            }
            onDelete={async () => {
              showDeleteModal({
                onSubmit: async () => {
                  await deleteAutomation(code);
                  navigate(ROUTE_PATHS.MY_AUTOMATION_LIST);
                }
              });
            }}
            onDuplicate={async formValues => {
              const response = await duplicateAutomation({
                code: automation.code,
                body: formValues
              });

              navigate(ROUTE_PATHS.NEW_AUTOMATION_REVIEW_ACTIVE(response.code));
            }}
          />
          <GiftPreviewBox
            giftWrapperUrl={automation?.experienceSettings?.branding?.giftWrapperUrl}
            previewLink={automation?.link?.preview}
            isModalShown={false}
            digitalGreetingCard={digitalGreetingCard}
          />

          {isMissingRecipientInfoModalOpen && automation && (
            <MissingRecipientInfoModal
              isOpen={isMissingRecipientInfoModalOpen}
              onRequestClose={setIsMissingRecipientInfoModalOpen}
              contacts={missingInfoContacts}
              occasionType={automation?.type}
              disabledColumns={[
                automation?.type === 'birthday' ? 'anniversary' : 'birthday',
                automation?.contacts?.tags?.length > 0 && 'actions'
              ]}
              deleteContact={id => {
                onUpdate({
                  params: {
                    code
                  },
                  body: {
                    contacts: {
                      ids: automation?.contacts?.ids?.filter(contact => contact.id !== id)
                    }
                  }
                });
              }}
            />
          )}
        </S.RightColumn>
      </S.PageContent>
    </PageLayout>
  );
}

export { AutomationReviewAndActiveTemplate };
